import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import User, { UserData } from "./steps/user";
import Payment, { PaymentData } from "./steps/payment";
import { logout } from "@/redux/slices/auth";
import { useNavigate } from "react-router-dom";
import {
  createSubscription,
  RequestCreateSubscriptionProps,
} from "@/service/plans";
import { Notyf } from "notyf";
import { resetPlanSelected } from "@/redux/slices/plan";
import { Companies } from "@/redux/slices/plan";
import CheckoutShell from "@/components/checkoutShell";
import OrderSummary from "@/components/orderSummary";
import Stepper from "@/components/stepper";
import CompaniesModal from "@/components/companiesModal";
import BrandHeader from "@/components/brandHeader";

export default function Checkout() {
  const dispatch = useAppDispatch();
  const planSelected = useAppSelector((state) => state.plan.planSelected);
  const auth = useAppSelector((state) => state.auth.auth);
  const navigation = useNavigate();
  const [companiesModal, setCompaniesModal] = useState<Companies[] | null>(
    null
  );
  const [progressScreen, setProgressScreen] = useState(1);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const goToUser = () => setProgressScreen(1);
  const goToPayment = () => setProgressScreen(2);

  const submitPayment = async (data: PaymentData, hashCard: string) => {
    setPaymentData(data);

    if (planSelected) {
      setSubmitLoading(true);
      const dataSubscription: RequestCreateSubscriptionProps = {
        planId: planSelected.id,
        customer: {
          type: userData?.type || "",
          name: userData?.name || "",
          document: userData?.cpf || "",
          email: userData?.email || "",
          phone: userData?.phone || "",
        },
        cardHash: hashCard,
        companyId: auth?.user?.IdCompany || 0,
        professionalId: auth?.user?.IdProfessional || 0,
      };

      await createSubscription(dataSubscription)
        .then(() => {
          dispatch(logout());
          dispatch(resetPlanSelected());
          navigation("/confirmation", { replace: true });
        })
        .finally(() => {
          setSubmitLoading(false);
        })
        .catch((error) => {
          const notyf = new Notyf({
            duration: 3000,
            position: { x: "center", y: "bottom" },
          });

          switch (error.status) {
            case 409:
              notyf.error("Você já possui um plano ativo");
              break;
            case 410:
              notyf.error(error.response.data.message);
              break;
            case 401:
              notyf.error("Pagamento não autorizado");
              break;
            default:
              notyf.error("Não foi possível assinar o plano, tente novamente");
          }
        });
    }
  };

  const submitUser = (data: UserData) => {
    setUserData(data);
    goToPayment();
  };

  const RenderComponent = useCallback(() => {
    switch (progressScreen) {
      case 1:
        return <User submitData={submitUser} userData={userData} />;
      case 2:
        return (
          <Payment
            submitData={submitPayment}
            backStep={goToUser}
            loading={submitLoading}
            paymentData={paymentData}
          />
        );
    }
  }, [progressScreen, userData, submitLoading, paymentData]);

  useEffect(() => {
    setTimeout(() => {
      window.HSStaticMethods.autoInit();
    }, 100);
  }, [progressScreen]);

  useEffect(() => {
    if (!planSelected) {
      navigation("/");
    }
  }, [planSelected]);

  if (!planSelected) return null;

  return (
    <>
      <CompaniesModal
        companies={companiesModal}
        onClose={() => setCompaniesModal(null)}
      />
      <CheckoutShell
        mobileSummary={
          <OrderSummary
            plan={planSelected}
            compact
            onShowCompanies={() =>
              setCompaniesModal(planSelected.listCompanies || [])
            }
          />
        }
        form={
          <div className="mx-auto flex w-full max-w-xl flex-col">
            <BrandHeader compact className="mb-8" />
            <Stepper step={progressScreen} />
            <RenderComponent />
          </div>
        }
        summary={
          <OrderSummary
            plan={planSelected}
            onShowCompanies={() =>
              setCompaniesModal(planSelected.listCompanies || [])
            }
          />
        }
      />
    </>
  );
}
